/* <recent-purchases>: the "Someone from Wembley bought … · 2 hours ago" pop-up (sections/recent-purchases).
   Entries are real orders (Flow-written shop metafield + "Earlier order" blocks). Sorts them newest first, drops
   any older than data-max-age days, then shows one after data-delay seconds and a new one every data-interval
   seconds, each for data-duration seconds. Stays hidden while a drawer, the quick view or search locks the page,
   holds while hovered or focused, and Close hides it for the rest of the visit. The theme editor shows the newest
   entry without timers. Cycling carries on from page to page (sessionStorage). */
if (!customElements.get('recent-purchases')) {
  const CLOSED_KEY = 'theme:recent-purchases:closed';
  const INDEX_KEY = 'theme:recent-purchases:next';
  const session = {
    get(key) { try { return sessionStorage.getItem(key); } catch { return null; } },
    set(key, value) { try { sessionStorage.setItem(key, value); } catch { /* storage blocked: fine */ } }
  };
  const readJSON = el => { try { return JSON.parse(el?.textContent || ''); } catch { return null; } };

  // "wembley" / "WEMBLEY" -> "Wembley"; mixed case is left as the customer typed it
  const townCase = town => {
    const t = String(town).trim().replace(/\s+/g, ' ');
    if (t !== t.toLowerCase() && t !== t.toUpperCase()) return t;
    return t.toLowerCase().replace(/(^|[\s'\-])(\p{L})/gu, (m, sep, ch) => sep + ch.toUpperCase());
  };

  customElements.define('recent-purchases', class RecentPurchases extends HTMLElement {
    connectedCallback() {
      this.strings = readJSON(this.querySelector('[data-recent-purchases-strings]')) || {};
      const now = Date.now();
      const maxAge = (+this.dataset.maxAge || 14) * 864e5;
      const seen = new Set();
      this.entries = (readJSON(this.querySelector('[data-recent-purchases]')) || [])
        .map(e => ({ ...e, city: townCase(e.city), time: /^\d+$/.test(e.at) ? e.at * 1000 : Date.parse(e.at) }))
        .filter(e => e.time && now - e.time < maxAge && e.time - now < 36e5)
        .sort((a, b) => b.time - a.time)
        .filter(e => { const k = `${e.city}|${e.url}|${e.time}`; if (seen.has(k)) return false; seen.add(k); return true; });
      if (!this.entries.length) return;

      this.link = this.querySelector('[data-recent-purchases-link]');
      this.image = this.querySelector('[data-recent-purchases-image]');
      this.media = this.querySelector('[data-recent-purchases-media]');

      if (this.hasAttribute('data-design-mode')) { this.fill(this.entries[0]); this.show(); return; }
      if (session.get(CLOSED_KEY)) return;
      if (this.hasAttribute('data-desktop-only') && matchMedia('(max-width: 749px)').matches) return;

      this.delay = (+this.dataset.delay || 5) * 1000;
      this.duration = (+this.dataset.duration || 6) * 1000;
      this.interval = Math.max((+this.dataset.interval || 10) * 1000, this.duration + 1000);
      this.index = (+session.get(INDEX_KEY) || 0) % this.entries.length;
      this.held = false;

      this.querySelector('[data-recent-purchases-close]').addEventListener('click', () => this.dismiss());
      this.addEventListener('mouseenter', () => { this.held = true; });
      this.addEventListener('mouseleave', () => { this.held = false; this.scheduleHide(); });
      this.addEventListener('focusin', () => { this.held = true; });
      this.addEventListener('focusout', e => { if (!this.contains(e.relatedTarget)) { this.held = false; this.scheduleHide(); } });

      // hide straight away when a drawer / quick view / search locks the page
      this.lockObserver = new MutationObserver(() => { if (this.blocked()) this.hide(); });
      this.lockObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });

      this.cycleTimer = setTimeout(() => this.next(), this.delay);
    }

    disconnectedCallback() { this.stop(); }

    blocked() { return document.body.classList.contains('is-scroll-locked') || document.hidden; }

    next() {
      if (!this.held && !this.blocked()) {
        this.fill(this.entries[this.index]);
        this.show();
        this.scheduleHide();
        this.index = (this.index + 1) % this.entries.length;
        session.set(INDEX_KEY, this.index);
      }
      this.cycleTimer = setTimeout(() => this.next(), this.interval);
    }

    fill(entry) {
      const s = this.strings;
      const who = this.querySelector('[data-recent-purchases-who]');
      const [before, after = ''] = (s.who || 'Someone from [city] bought').split('[city]');
      const strong = document.createElement('strong');
      strong.textContent = entry.city;
      who.replaceChildren(before, strong, after);
      this.querySelector('[data-recent-purchases-product]').textContent = entry.title;
      this.querySelector('[data-recent-purchases-time]').textContent = this.ago(entry.time);
      this.link.href = entry.url;
      this.media.style.setProperty('--product-colour', entry.colour || '');
      if (entry.image) this.image.src = entry.image;
      this.image.alt = '';
    }

    ago(time) {
      const s = this.strings;
      const minutes = Math.floor(Math.max(0, Date.now() - time) / 6e4);
      const pick = (n, one, other) => (n === 1 ? one : other).replace('[count]', n);
      if (minutes < 1) return s.justNow || 'Just now';
      if (minutes < 60) return pick(minutes, s.minute || '1 minute ago', s.minutes || '[count] minutes ago');
      const hours = Math.floor(minutes / 60);
      if (hours < 24) return pick(hours, s.hour || '1 hour ago', s.hours || '[count] hours ago');
      const days = Math.floor(hours / 24);
      return pick(days, s.day || '1 day ago', s.days || '[count] days ago');
    }

    show() { this.inert = false; this.classList.add('is-visible'); }

    hide() {
      clearTimeout(this.hideTimer);
      if (this.contains(document.activeElement)) document.activeElement.blur();
      this.held = false;
      this.classList.remove('is-visible');
      this.inert = true;
    }

    scheduleHide() {
      clearTimeout(this.hideTimer);
      this.hideTimer = setTimeout(() => { if (!this.held) this.hide(); }, this.duration);
    }

    dismiss() {
      session.set(CLOSED_KEY, '1');
      this.hide();
      this.stop();
    }

    stop() {
      clearTimeout(this.cycleTimer);
      clearTimeout(this.hideTimer);
      this.lockObserver?.disconnect();
    }
  });
}
